import React, { useState, useCallback, useRef } from 'react';
import { CheckCircle2, Archive, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import axios from '@/lib/http';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ZipUploadResult {
  status: string;
  files_processed: number;
  files_failed: number;
  total_files: number;
  processing_time_seconds: number;
  categorized_documents: {
    K1: any[];
    '1065': any[];
    PL: any[];
    BANK: any[];
    UNKNOWN: any[];
    ERRORS: any[];
  };
  summary: {
    total_documents: number;
    by_type: {
      K1: number;
      '1065': number;
      'P&L': number;
      'Bank Statements': number;
      Unknown: number;
    };
    errors: number;
    ready_for_wizard: boolean;
  };
  // TS-FU3 archaeology (2026-04-29): consumed by UnifiedUploader.tsx:746
  // (`result.canonical_data?.periods`, `result.tax1065Files`). Backend
  // payload includes them; the type had dropped them in a refactor.
  canonical_data?: { periods?: any[] } & Record<string, any>;
  tax1065Files?: any[];
}

interface ZipBatchUploadProps {
  onUploadComplete?: (result: ZipUploadResult) => void;
  onClose?: () => void;
  reportId?: string;
}

export default function ZipBatchUpload({ onUploadComplete, onClose, reportId }: ZipBatchUploadProps) {
  const { getToken } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState<ZipUploadResult | null>(null);
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const zipFile = files.find(f => f.name.toLowerCase().endsWith('.zip'));

    if (zipFile) {
      setSelectedFile(zipFile);
      setError('');
    } else {
      setError('Please drop a ZIP file');
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.zip')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a ZIP file');
      }
    }
  };

  const uploadZipFile = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      const url = reportId 
        ? `${BACKEND_URL}/api/v1/documents/upload-zip?report_id=${reportId}`
        : `${BACKEND_URL}/api/v1/documents/upload-zip`;

      const response = await axios.post(url, formData, config);

      setResult(response.data);
      setIsUploading(false);
      
      if (onUploadComplete) {
        onUploadComplete(response.data);
        // Auto-close modal after successful upload to show data review
        if (onClose) {
          onClose();
        }
      }
    } catch (err: any) {
      console.error('ZIP upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setResult(null);
    setError('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="sticky top-0 p-6 rounded-t-2xl" style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Archive className="w-9 h-9" />
              <div>
                <h2 className="text-2xl font-bold">Batch Upload</h2>
                <p className="text-sm" style={{ color: 'var(--color-on-brand)', opacity: 0.85 }}>Drop your ZIP → We'll handle the rest</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="transition"
                style={{ color: 'var(--color-on-brand)' }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {!result ? (
            <>
              {/* Upload Area */}
              <div
                className="border-3 border-dashed rounded-xl p-12 text-center transition-all"
                style={
                  isDragging
                    ? { borderColor: 'var(--color-brand)', backgroundColor: 'var(--color-needs-human-tint)', transform: 'scale(1.05)' }
                    : selectedFile
                    ? { borderColor: 'var(--color-verified)', backgroundColor: 'transparent' }
                    : { borderColor: 'var(--color-border)' }
                }
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {selectedFile ? (
                  <div className="space-y-4">
                    <CheckCircle2 className="w-16 h-16 mx-auto" style={{ color: 'var(--color-verified)' }} />
                    <div>
                      <p className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{selectedFile.name}</p>
                      <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={reset}
                      className="text-sm underline"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Choose different file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Archive className="w-16 h-16 mx-auto animate-bounce" style={{ color: 'var(--color-text-dim)' }} />
                    <div>
                      <p className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                        Drag & Drop your ZIP file here
                      </p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>
                        or click below to browse
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileSelect}
                className="hidden"
              />

              {!selectedFile && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full mt-4 py-3 rounded-lg font-semibold transition"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  Browse Files
                </button>
              )}

              {selectedFile && !isUploading && (
                <button
                  onClick={uploadZipFile}
                  className="w-full mt-4 py-4 rounded-lg font-bold text-lg transition shadow-lg"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  Process {selectedFile.name}
                </button>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Processing...</span>
                    <span className="font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{uploadProgress}%</span>
                  </div>
                  <div className="w-full rounded-full h-3 overflow-hidden" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-300 relative overflow-hidden"
                      style={{ width: `${uploadProgress}%`, backgroundColor: 'var(--color-brand)' }}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{ borderColor: 'var(--color-brand)', borderTopColor: 'transparent' }}></div>
                    <span>Extracting, categorizing, and processing files...</span>
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
                  <p className="font-semibold flex items-center gap-1.5" style={{ color: 'var(--color-needs-human-text)' }}><AlertTriangle className="w-4 h-4" /> {error}</p>
                </div>
              )}

              {/* Info */}
              <div className="mt-6 p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-text-secondary)' }}>
                <p className="font-semibold mb-2">What we'll process:</p>
                <ul className="space-y-1 ml-4">
                  <li>• K-1 forms (Schedule K-1)</li>
                  <li>• Tax returns (Form 1065, 1120)</li>
                  <li>• P&L statements</li>
                  <li>• Bank statements</li>
                  <li>• Supporting documents</li>
                </ul>
                <p className="mt-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  Max 100 files per ZIP • Max 100MB • Supported: PDF, Excel
                </p>
              </div>
            </>
          ) : (
            /* Results View */
            <div className="space-y-6">
              {/* Success Banner */}
              <div className="border-2 rounded-xl p-6" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-14 h-14 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--color-verified)' }}>Processing Complete!</h3>
                    <p className="mb-3" style={{ color: 'var(--color-verified)' }}>
                      Successfully processed <strong>{result.files_processed}</strong> of{' '}
                      <strong>{result.total_files}</strong> files in{' '}
                      <strong>{result.processing_time_seconds}s</strong>
                    </p>
                    {result.files_failed > 0 && (
                      <p className="text-sm flex items-center gap-1.5" style={{ color: 'var(--color-brand)' }}>
                        <AlertTriangle className="w-4 h-4" /> {result.files_failed} file(s) could not be processed
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Document Summary */}
              <div className="border rounded-xl p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <h4 className="font-bold text-lg mb-4">
                  Documents Categorized
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(result.summary.by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>{type}</span>
                      <span className="font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    reset();
                    if (onClose) onClose();
                  }}
                  className="flex-1 py-3 rounded-lg font-semibold transition"
                  style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                >
                  Close
                </button>
                <button
                  onClick={reset}
                  className="flex-1 py-3 rounded-lg font-semibold transition"
                  style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
                >
                  Upload Another ZIP
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
